import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { appActions } from '../../../store';
import ProductList from '../../shared/ProductList/ProductList';
import { generateThumbnail } from '../../../Helpers/ThumbHelper';
import styles from './ColorSlotSelector.module.css';
import plus from './assets/plus.svg';
import trash from './assets/trash.svg';

export default function ColorSlotSelector({ descriptor, activeProfile }) {
    const dispatch = useDispatch();
    const productSelection = useSelector(state => state.app.productSelection);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [baseProducts, setBaseProducts] = useState([]);
    const [accentProducts, setAccentProducts] = useState([]);
    const [runningLineProducts, setRunningLineProducts] = useState([]);
    const [additionalColorProducts, setAdditionalColorProducts] = useState([]);
    const [accentTab, setAccentTab] = useState('additional-colors'); // 'running-line' or 'additional-colors'
    const [searchTerm, setSearchTerm] = useState('');
    const [colorFamily, setColorFamily] = useState('');

    // Fetch base products (series 9701)
    useEffect(() => {
        fetch('https://lizard.chameleonpower.com/CDN/ProductsBySeries/9701.json')
            .then(res => res.json())
            .then(data => setBaseProducts(Array.isArray(data) ? data : []))
            .catch(err => console.error('Error fetching base products:', err));
    }, []);

    // Fetch running line products (series 9697)
    useEffect(() => {
        fetch('https://lizard.chameleonpower.com/CDN/ProductsBySeries/9697.json')
            .then(res => res.json())
            .then(data => setRunningLineProducts(Array.isArray(data) ? data : []))
            .catch(err => console.error('Error fetching running line products:', err));
    }, []);

    // Fetch additional color products (series 9698)
    useEffect(() => {
        fetch('https://lizard.chameleonpower.com/CDN/ProductsBySeries/9698.json')
            .then(res => res.json())
            .then(data => setAdditionalColorProducts(Array.isArray(data) ? data : []))
            .catch(err => console.error('Error fetching additional color products:', err));
    }, []);

    // Set accentProducts based on active tab
    useEffect(() => {
        if (accentTab === 'running-line') {
            setAccentProducts(runningLineProducts);
        } else {
            setAccentProducts(additionalColorProducts);
        }
    }, [accentTab, runningLineProducts, additionalColorProducts]);

    // Parse descriptor to get color slots
    const colorSlots = descriptor.split(',').map(slot => slot.trim());

    const handleSlotClick = (index) => {
        setSelectedSlot(index);
        console.log('Selected slot:', index, colorSlots[index], 'isBase:', index === 0);
    };

    const handleProductSelect = (product) => {
        if (selectedSlot === null) return;

        // Update product selection for the selected slot
        const newSelection = [...productSelection];
        while (newSelection.length <= selectedSlot) {
            newSelection.push(-1);
        }
        newSelection[selectedSlot] = product.id;
        dispatch(appActions.setProductSelection(newSelection));

        console.log('Product selected for slot', selectedSlot, ':', product);
    };

    const getSlotLabel = (slot, index) => {
        if (index === 0) return 'BASE';
        return `Color ${index}`;
    };

    const getSlotSubLabel = (slot, index) => {
        if (!isSlotFilled(index)) {
            return slot; // Show descriptor name if no product selected
        }

        const isBase = index === 0;
        const productId = productSelection[index];
        const product = getProductById(productId, isBase);

        if (!product) return slot;

        // Combine name and color
        const name = product.name || '';
        const color = product.color || '';

        if (name && color) {
            return `${name} ${color}`;
        }
        return name || color || slot;
    };

    const isSlotFilled = (index) => {
        return productSelection[index] && productSelection[index] !== -1;
    };

    const getProductById = (productId, isBase) => {
        if (isBase) {
            return baseProducts.find(p => p.id === productId);
        }

        // For accent colors, search in both running line and additional colors
        const product = runningLineProducts.find(p => p.id === productId) ||
            additionalColorProducts.find(p => p.id === productId);
        return product;
    };

    const getSlotThumbnail = (index) => {
        if (!isSlotFilled(index)) return null;

        const isBase = index === 0;
        const productId = productSelection[index];
        const product = getProductById(productId, isBase);

        if (!product) return null;

        // Determine series ID based on which array contains the product
        let seriesId = '9701'; // default to base
        if (!isBase) {
            if (runningLineProducts.find(p => p.id === productId)) {
                seriesId = '9697';
            } else if (additionalColorProducts.find(p => p.id === productId)) {
                seriesId = '9698';
            }
        }

        // Use size=300 and focus=1200 for base products, size=90 and focus=500 for accents
        const size = isBase ? '300' : '90';
        const focus = isBase ? 1200 : 500;
        return generateThumbnail(product.id, product.specifier, size, focus, seriesId);
    };

    const isBaseSlot = selectedSlot === 0;

    // Get unique color families for dropdown (from additional colors only)
    const colorFamilies = [...new Set(additionalColorProducts.map(p => p.color).filter(Boolean))].sort();

    // Build filters for additional colors tab
    const getAdditionalColorsFilters = () => {
        const filters = {};
        if (colorFamily) {
            filters.color = colorFamily;
        }
        return filters;
    };

    return (
        <div className={styles.colorSlotContainer}>
            <div className={styles.slotGrid}>
                {colorSlots.map((slot, index) => {
                    const thumbnailUrl = getSlotThumbnail(index);

                    return (
                        <div
                            key={index}
                            className={`${styles.colorSlot} ${selectedSlot === index ? styles.selected : ''}`}
                            onClick={() => handleSlotClick(index)}
                        >
                            <div className={styles.colorPreview}>
                                {thumbnailUrl ? (
                                    <img
                                        src={thumbnailUrl}
                                        alt={`${getSlotLabel(slot, index)} color`}
                                        className={styles.thumbnailImage}
                                    />
                                ) : (
                                    <div className={styles.colorBox}>
                                      <img src={plus} className={styles.plusImg} width="100" height="100" alt="product thumbnail"/>
                                    </div>
                                )}
                            </div>

                            <div className={styles.flexDiv}>
                                <div className={styles.slotInfo}>
                                    <div className={styles.slotLabel}>
                                        {getSlotLabel(slot, index)}
                                    </div>
                                    <div className={styles.slotSubLabel}>
                                        {getSlotSubLabel(slot, index)}
                                    </div>
                                </div>

                                <div className={styles.slotActions}>
                                    {isSlotFilled(index) && (
                                        <button
                                            className={styles.clearButton}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const newSelection = [...productSelection];
                                                newSelection[index] = -1;
                                                dispatch(appActions.setProductSelection(newSelection));
                                            }}
                                            aria-label="Clear selection"
                                        >
                                                <img src={trash} width="20" height="20" alt="Delete Item"/>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {selectedSlot !== null && (
                <div className={styles.productSelector}>
                    {isBaseSlot ? (
                        <ProductList
                            seriesId="9701"
                            thumbSize="300"
                            focus={1200}
                            titleAttribute="color"
                            descriptionAttribute=""
                            filters={{ name: activeProfile.name }}
                            onProductSelect={handleProductSelect}
                            gridClassName="twoColumn"
                        />
                    ) : (
                        <div className={styles.accentSelector}>
                            {/* Tab container hidden - uncomment to re-enable running line tab
                            <div className={styles.tabContainer}>
                                <button
                                    className={`${styles.tab} ${accentTab === 'running-line' ? styles.activeTab : ''}`}
                                    onClick={() => setAccentTab('running-line')}
                                >
                                    Running Line
                                </button>
                                <button
                                    className={`${styles.tab} ${accentTab === 'additional-colors' ? styles.activeTab : ''}`}
                                    onClick={() => setAccentTab('additional-colors')}
                                >
                                    Additional Colors
                                </button>
                            </div>
                            */}

                            {accentTab === 'running-line' ? (
                                <ProductList
                                    seriesId="9697"
                                    thumbSize="256"
                                    titleAttribute="name"
                                    descriptionAttribute="color"
                                    onProductSelect={handleProductSelect}
                                />
                            ) : (
                                <div className={styles.additionalColorsContent}>
                                    <div className={styles.filterBar}>
                                        <div className={styles.searchWrap}>
                                            <input
                                                type="text"
                                                placeholder="Search..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className={styles.searchInput}
                                            />
                                            <span className={styles.searchIcon} aria-hidden="true">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                                    <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" strokeWidth="2" />
                                                </svg>
                                            </span>
                                        </div>
                                        <select
                                            value={colorFamily}
                                            onChange={(e) => setColorFamily(e.target.value)}
                                            className={styles.colorFamilyDropdown}
                                        >
                                            <option value="">Color Family</option>
                                            {colorFamilies.map(family => (
                                                <option key={family} value={family}>{family}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <ProductList
                                        seriesId="9698"
                                        thumbSize="256"
                                        titleAttribute="name"
                                        descriptionAttribute=""
                                        filters={getAdditionalColorsFilters()}
                                        searchValue={searchTerm}
                                        searchColumns={['name', 'color', 'description']}
                                        transformTitle={(title) => title.replace(/Digital Color/gi, '').replace(/PANTONE/gi, '').trim()}
                                        onProductSelect={handleProductSelect}
                                        priorityIds={[6173599, 6173600, 6173601, 6173602, 6173603, 6173604, 6173605, 6173606, 6173607, 6173608, 6173609, 6173610]}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
